import { TZDate } from '@date-fns/tz';
import { endOfDay, format, startOfDay } from 'date-fns';

/** Parses a date value to Date, returns null if invalid */
export function parseDate(date: string | Date | number | null | undefined): Date | null {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date as string | number);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a date in the browser's local timezone using date-fns.
 * Since `format` uses the local timezone, UTC dates are automatically converted.
 */
export function formatLocalDateTime(date: Date, formatStr = 'dd MMM yyyy, HH:mm:ss'): string {
  return format(date, formatStr);
}

/**
 * Formats a date in a specific IANA timezone using Intl.DateTimeFormat.
 * Returns a consistent "dd MMM yyyy, HH:mm:ss" style string.
 */
export function formatInTimezone(date: Date, timezone: string, includeTime = true): string {
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false } : {}),
  };

  const parts: Record<string, string> = {};
  new Intl.DateTimeFormat('en-GB', opts).formatToParts(date).forEach((p) => {
    parts[p.type] = p.value;
  });

  const datePart = `${parts.day} ${parts.month} ${parts.year}`;
  if (!includeTime) return datePart;
  return `${datePart}, ${parts.hour}:${parts.minute}:${parts.second}`;
}

/** Returns the browser's IANA timezone identifier */
export function getLocalTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Returns the short timezone label (e.g. "GMT+3", "EST") for a given IANA timezone */
export function getTimezoneShortLabel(timezone: string): string {
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value ?? timezone
  );
}

export function startOfDayInTz(date: Date, timezone: string): Date {
  return startOfDay(new TZDate(date.getFullYear(), date.getMonth(), date.getDate(), timezone));
}

export function endOfDayInTz(date: Date, timezone: string): Date {
  return endOfDay(new TZDate(date.getFullYear(), date.getMonth(), date.getDate(), timezone));
}

/**
 * Extracts the calendar date of `date` as seen in `timezone`, then returns a
 * browser-local Date whose getFullYear/getMonth/getDate reflect that calendar date.
 * Useful for re-encoding a UTC timestamp's calendar day into a different timezone:
 *   e.g. 2026-04-07T21:00:00Z in Istanbul = April 8 -> new Date(2026, 3, 8)
 */
export function toLocalDateInTz(date: Date, timezone: string): Date {
  const d = new TZDate(date, timezone);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
